import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@redpanda/ui';

interface ColorPickerFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  variant?: 'default' | 'error';
  disabled?: boolean;
  predefinedColors?: string[];
  // 移除 [key: string]: any; 避免與 react-hook-form 衝突
}

const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  id,
  value,
  onChange,
  variant = 'default',
  disabled = false,
  predefinedColors = [
    '#3B82F6', // 藍色
    '#EF4444', // 紅色
    '#10B981', // 綠色
    '#F59E0B', // 黃色
    '#8B5CF6', // 紫色
    '#F97316', // 橙色
    '#06B6D4', // 青色
    '#84CC16', // 萊姆綠
    '#EC4899', // 粉紅色
    '#6B7280', // 灰色
  ]
}) => {
  const { t } = useTranslation();

  return (
    <div>
      {/* 預設顏色選項 */}
      <div className="mb-3">
        <div className="flex flex-wrap gap-2">
          {predefinedColors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => !disabled && onChange(color)}
              disabled={disabled}
              className={`w-8 h-8 rounded-full border-2 ${
                value === color 
                  ? 'border-gray-900 dark:border-white ring-2 ring-offset-2 ring-gray-900/20 dark:ring-white/30 dark:ring-offset-slate-800'
                  : 'border-gray-300 dark:border-slate-600'
              } transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      {/* 自定義顏色 */}
      <div className="flex items-center space-x-3">
        <input
          type="color"
          id={`${id}-color`}
          value={value || '#3B82F6'}
          onChange={(e) => !disabled && onChange(e.target.value)}
          disabled={disabled}
          className="w-12 h-10 border border-gray-300 dark:border-slate-600 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <Input
          id={id}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#3B82F6"
          className="flex-1"
          maxLength={7}
          disabled={disabled}
          variant={variant}
        />
      </div>
      
      {/* 顏色預覽 */}
      <div className="mt-3 flex items-center space-x-2">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('customers.detail.tags.preview', { ns: 'pages' })}
        </span>
        {/* 和標籤實際顯示的樣子一致：中性框線膠囊＋顏色小圓點（不再用白字配彩色底，黃色等淺色對比不足） */}
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] border border-line text-fg">
          <span className="h-2 w-2 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/25" style={{ backgroundColor: value || '#3B82F6' }} />
          {t('tags.add.form.preview', { ns: 'pages' })}
        </span>
      </div>
    </div>
  );
};

export default ColorPickerField;